import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { Autocomplete, Box, Button, CircularProgress, Collapse, Grid, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import {
  BUTTON_TEXT_CANCEL,
  BUTTON_TEXT_UPDATE,
  COUNTRY_OPTION_US,
  LABEL_CITY,
  LABEL_COUNTRY,
  LABEL_NOT_AVAILABLE,
  LABEL_STATE,
  LABEL_STREET_ADDRESS,
  LABEL_ZIP_CODE
} from '../../../../shared/lib/constants';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import StateSelector from '../../../../shared/lib/common/StateSelector';
import palette from '../../../../shared/lib/theme/palette';
import { UserDetailHeading, userDetailsWrapper } from '../../../../shared/lib/theme/styleConstants';
import { COUNTRIES, MSG_BILLING_ADDRESS_UPDATED } from '../../app/constants';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { useAppContext } from '../../app/contexts/AppContext';
import { Alert } from '../../../../shared/lib/common/Alert';
import { yupResolver } from '@hookform/resolvers/yup';
import { editBillingInfoValidationSchema } from '../../app/validationSchema';
import { EditBillingInfoInput } from '../../app/interfaceTypes';
import { Card, useFetchBillingDetailsLazyQuery, useUpdateCardDetailMutation } from '../../generated';
import { convert_US_TO_USA } from '../../../../shared/lib/utils';
import { useMobileView } from '../../app/hooks/useMobileView';

export const BillingAddress = () => {
  countries.registerLocale(enLocale);

  const mediumViewport = useMobileView();
  const { isBillingAddress, dispatch, currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};
  const { borderLight, blackOne } = palette;
  const [address, setAddress] = useState<Card | undefined>(undefined);
  const { address_city, address_country, address_line1, address_line2, address_state, address_zip } = address ?? {};
  const [streetAddress, setStreetAddress] = useState<string>('');
  const billingName = address?.name;
  const names = billingName?.split(' ');
  const [firstName, setFirstName] = useState<string | undefined>(names?.[0]);
  const [lastName, setLastName] = useState<string | undefined>(names?.[1]);
  const fullName = firstName + ' ' + lastName;

  const handleOpen = () => {
    dispatch({ type: ActionType.SET_IS_BILLING_ADDRESS, isBillingAddress: !isBillingAddress });
  };

  const handleClose = () => {
    resetFields();
    refetch();
    dispatch({ type: ActionType.SET_IS_BILLING_ADDRESS, isBillingAddress: !isBillingAddress });
  };

  const methods = useForm<EditBillingInfoInput, any, EditBillingInfoInput>({
    mode: 'all',
    // @ts-expect-error  //TODO: THIS ERROR IS BEING SUPPRESSED WE NEED TO FIX THIS LATER 8)
    resolver: yupResolver(editBillingInfoValidationSchema),

    defaultValues: {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      address_city: '',
      address_country: COUNTRY_OPTION_US.label,
      address_line1: '',
      address_line2: '',
      address_state: '',
      address_zip: ''
    }
  });

  const [getBillingDetail, { refetch }] = useFetchBillingDetailsLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchCardDetails } = data;
      setAddress(fetchCardDetails ?? undefined);
    }
  });

  const [updateBillingDetail, { loading }] = useUpdateCardDetailMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { updateCardDetail } = data;
      const { name, address_line1, address_line2 } = updateCardDetail;

      if (address_line2 === null || undefined) address_line1 && setStreetAddress(address_line1);
      else setStreetAddress(`${address_line1} ${address_line2}`);

      setAddress(updateCardDetail as Card);
      refetch();
      handleClose();

      if (name) {
        const splitNames = fullName.split(' ');
        setFirstName(splitNames[0]);
        setLastName(fullName.slice(fullName.indexOf(' ') + 1));
        handleClose();
        resetFields();
      }
      Alert.success(MSG_BILLING_ADDRESS_UPDATED);
    }
  });

  const { handleSubmit, setValue, reset, control } = methods;

  const resetFields = () => {
    reset({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      address_city: address_city ?? '',
      address_country: convert_US_TO_USA(address_country ?? ''),
      address_zip: address_zip ?? '',
      address_line1: address_line1 ?? '',
      address_line2: address_line2 ?? '',
      address_state: address_state ?? ''
    });
  };

  useEffect(() => {
    firstName && setValue('firstName', firstName);
    lastName && setValue('lastName', lastName);
    address_state && setValue('address_state', address_state);
    address_country && setValue('address_country', convert_US_TO_USA(address_country ?? ''));
    address_zip && setValue('address_zip', address_zip);
    !address_zip && setValue('address_zip', '');
    address_city && setValue('address_city', address_city);
    address_line1 && setValue('address_line1', address_line1);
    address_line2 && setValue('address_line2', address_line2);
    !address_line2 && setValue('address_line2', '');
  }, [firstName, lastName, address_state, address_country, address_zip, address_city, address_line1, address_line2]);

  useEffect(() => {
    if (address_line1 && address_line2) setStreetAddress(`${address_line1} ${address_line2}`);
    else if (address_line1) setStreetAddress(`${address_line1}`);
  }, [address_line1, address_line2]);

  const fetchBillingDetail = async (customerStripeId: string) => {
    await getBillingDetail({
      variables: {
        fetchCardDetailsInput: customerStripeId
      }
    });
  };

  useEffect(() => {
    if (customerStripeId) {
      (async () => await fetchBillingDetail(customerStripeId))();
    }
  }, [customerStripeId]);

  const onSubmit: SubmitHandler<EditBillingInfoInput> = async (data) => {
    const { address_line1, address_line2, address_state, address_zip, address_country, address_city } = data;

    customerStripeId &&
      (await updateBillingDetail({
        variables: {
          updateCardDetails: {
            address_zip: address_zip,
            address_line1,
            address_line2: address_line2 || '',
            address_state,
            address_city,
            address_country: countries.getAlpha2Code(address_country ?? 'US', 'en'),
            customerStripeId
          }
        }
      }));
  };

  return (
    <Box marginTop="16px">
      <Box sx={userDetailsWrapper} marginBottom="24px">
        <Collapse in={isBillingAddress}>
          <Box>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container columnSpacing={mediumViewport ? 1 : 2} rowSpacing={1}>
                  <Grid item xs={12}>
                    <CustomController controllerLabel="Street Address Line 1" controllerName="address_line1" />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomController controllerLabel="Street Address Line 2" controllerName="address_line2" />
                  </Grid>

                  <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                    <Controller
                      name="address_country"
                      control={control}
                      rules={{
                        required: 'Country is required'
                      }}
                      render={({ field, fieldState }) => (
                        <Autocomplete
                          {...field}
                          sx={{ marginTop: '8px' }}
                          options={COUNTRIES}
                          renderInput={(params) => <TextField {...params} label={LABEL_COUNTRY} error={!!fieldState.error} helperText={fieldState.error?.message} />}
                          onChange={(_, data) => field.onChange(data)}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                    <StateSelector name="address_state" label={LABEL_STATE} />
                  </Grid>

                  <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                    <CustomController controllerLabel={LABEL_CITY} controllerName="address_city" />
                  </Grid>

                  <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                    <CustomController controllerLabel={LABEL_ZIP_CODE} controllerName="address_zip" />
                  </Grid>
                </Grid>
                <Box display="flex" gap="16px" mt={2}>
                  <Button onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                    {BUTTON_TEXT_CANCEL}
                  </Button>

                  <Button type="submit" variant="contained" color="primary" disabled={loading} endIcon={loading && <CircularProgress size={20} />}>
                    {BUTTON_TEXT_UPDATE}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        </Collapse>

        <Collapse in={!isBillingAddress}>
          <Box>
            <Grid container spacing={2} rowSpacing={4}>
              <Grid item xs={12}>
                <Typography variant="h2" sx={UserDetailHeading}>
                  {LABEL_STREET_ADDRESS}
                </Typography>
                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                  {streetAddress || LABEL_NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                <Typography variant="h2" sx={UserDetailHeading}>
                  {LABEL_COUNTRY}
                </Typography>
                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                  {(address_country && (countries.getName(address_country, 'en') || address_country)) || LABEL_NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                <Typography variant="h2" sx={UserDetailHeading}>
                  {LABEL_STATE}
                </Typography>
                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                  {address_state || LABEL_NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                <Typography variant="h2" sx={UserDetailHeading}>
                  {LABEL_CITY}
                </Typography>
                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                  {address_city || LABEL_NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={mediumViewport ? 6 : 12} md={6}>
                <Typography variant="h2" sx={UserDetailHeading}>
                  {LABEL_ZIP_CODE}
                </Typography>
                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                  {address_zip || LABEL_NOT_AVAILABLE}
                </Typography>
              </Grid>
            </Grid>

            <Button onClick={handleOpen} variant="text" sx={{ border: `1px solid ${borderLight}`, marginTop: 4 }}>
              Edit billing address
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
